// Universal Sector & Asset Category Categorizer
export interface SectorInfo {
  key: string;
  nameSk: string;
  nameEn: string;
  icon: string;
  color: string;
  badgeBg: string;
  borderClass: string;
  selectedBgClass: string;
  glowClass: string;
}

export const getAssetCategory = (symbol: string, name: string, sector?: string, language: 'sk' | 'en' = 'en'): string => {
  const s = (symbol || '').toUpperCase().trim();
  const n = (name || '').toUpperCase().trim();
  const sec = (sector || '').toUpperCase().trim();

  // 1. Basic Materials & Mining
  if (
    s.includes('AGL') || n.includes('AGNICO') || n.includes('MINING') || n.includes('GOLD') || 
    n.includes('SILVER') || sec.includes('BASIC MATERIALS') || sec.includes('MINING') || sec.includes('MATERIALS')
  ) {
    return language === 'sk' ? '⛏️ Základné materiály & Ťažba' : '⛏️ Basic Materials & Mining';
  }

  // 2. Defense & Security
  if (
    s.includes('CSG') || n.includes('CZECHOSLOVAK') || 
    s.includes('LMT') || n.includes('LOCKHEED') || 
    s.includes('RHM') || n.includes('RHEINMETALL') ||
    s.includes('NOC') || s.includes('RTX') || s.includes('BA') ||
    sec.includes('DEFENSE') || sec.includes('AEROSPACE') || n.includes('DEFENSE')
  ) {
    return language === 'sk' ? '🛡️ Zbrojárstvo & Bezpečnosť' : '🛡️ Defense & Security';
  }

  // 3. Biotech, Pharmaceuticals & Healthcare
  if (
    s.includes('SLS') || n.includes('SELLAS') ||
    s.includes('DFTX') || n.includes('DEFINIUM') ||
    s.includes('SYRE') || n.includes('SPYRE') ||
    s.includes('PFE') || n.includes('PFIZER') || 
    s.includes('JNJ') || n.includes('JOHNSON') || 
    s.includes('LLY') || n.includes('LILLY') || 
    s.includes('MRK') || s.includes('ABBV') || s.includes('AZN') ||
    sec.includes('HEALTHCARE') || sec.includes('BIOTECH') || sec.includes('PHARMA') ||
    n.includes('PHARMA') || n.includes('BIOTECH') || n.includes('THERAPEUTICS') || n.includes('LIFE SCIENCE')
  ) {
    return language === 'sk' ? '🧬 Biotech & Zdravotníctvo' : '🧬 Biotech & Healthcare';
  }

  // 4. Automotive & Consumer Cyclical
  if (
    s.includes('TSLA') || n.includes('TESLA') ||
    s.includes('BMW') || n.includes('BMW') ||
    s.includes('MBG') || n.includes('MERCEDES') ||
    s.includes('VOW') || n.includes('VOLKSWAGEN') ||
    s.includes('RACE') || n.includes('FERRARI') ||
    s.includes('AMZN') || n.includes('AMAZON') ||
    sec.includes('CONSUMER CYCLICAL') || sec.includes('AUTOMOTIVE')
  ) {
    return language === 'sk' ? '🚗 Automobilový & Spotrebný' : '🚗 Automotive & Consumer';
  }

  // 5. Semiconductors, Hardware, Optical Tech & Memory
  if (
    s.includes('LITE') || n.includes('LUMENTUM') || 
    s.includes('SNDK') || n.includes('SANDISK') || 
    s.includes('SKHY') || n.includes('SK HYNIX') || s.includes('000660') ||
    s.includes('MU') || n.includes('MICRON') || 
    s.includes('WDC') || n.includes('WESTERN DIGITAL') || 
    s.includes('AAPL') || s.includes('NVDA') || s.includes('MSFT') || 
    s.includes('GOOGL') || s.includes('TSMC') || s.includes('TSM') || 
    s.includes('AMD') || s.includes('INTC') || s.includes('ASML') ||
    sec.includes('TECHNOLOGY') || sec.includes('SEMICONDUCTOR') ||
    n.includes('SEMICONDUCTOR') || n.includes('HARDWARE') || n.includes('TECH')
  ) {
    return language === 'sk' ? '💻 Technológie & Hardvér' : '💻 Technology & Hardware';
  }

  // 6. Banking & Financial Institutions
  if (
    s.includes('JPM') || s.includes('BAC') || s.includes('V') || 
    s.includes('MA') || s.includes('GS') || s.includes('MS') ||
    sec.includes('FINANCE') || sec.includes('FINANCIAL') ||
    n.includes('BANK') || n.includes('FINAN') || n.includes('CAPITAL')
  ) {
    return language === 'sk' ? '🏦 Financie & Bankovníctvo' : '🏦 Banking & Finance';
  }

  // 7. Energy & Commodities
  if (
    s.includes('XOM') || s.includes('CVX') || s.includes('SHEL') || s.includes('TTE') || 
    sec.includes('ENERGY') || n.includes('ENERGY') || n.includes('OIL') || n.includes('GAS')
  ) {
    return language === 'sk' ? '⚡ Energetika & Infraštruktúra' : '⚡ Energy & Infrastructure';
  }

  // 8. Crypto & Digital Assets
  if (s.includes('BTC') || s.includes('ETH') || s.includes('SOL') || sec.includes('CRYPTO') || n.includes('BITCOIN')) {
    return language === 'sk' ? '🪙 Kryptomeny' : '🪙 Cryptocurrencies';
  }

  // Default to Technology & Hardware
  return language === 'sk' ? '💻 Technológie & Hardvér' : '💻 Technology & Hardware';
};

export const getSectorMeta = (catStr: string): SectorInfo => {
  const c = catStr.toLowerCase();
  if (c.includes('zbrojár') || c.includes('defense') || c.includes('security')) {
    return {
      key: 'defense',
      nameSk: 'Zbrojárstvo & Bezpečnosť',
      nameEn: 'Defense & Security',
      icon: '🛡️',
      color: '#1e293b', // slate-800
      badgeBg: 'bg-slate-900 text-white',
      borderClass: 'border-slate-400/80 hover:border-slate-800',
      selectedBgClass: 'bg-slate-900 text-white border-slate-900 shadow-md ring-2 ring-slate-600',
      glowClass: 'bg-slate-500/10 text-slate-800'
    };
  }
  if (c.includes('technol') || c.includes('technology') || c.includes('hardw')) {
    return {
      key: 'tech',
      nameSk: 'Technológie & Hardvér',
      nameEn: 'Technology & Hardware',
      icon: '💻',
      color: '#4f46e5', // indigo-600
      badgeBg: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      borderClass: 'border-indigo-200 hover:border-indigo-500',
      selectedBgClass: 'bg-indigo-950 text-white border-indigo-700 shadow-md ring-2 ring-indigo-500',
      glowClass: 'bg-indigo-500/10 text-indigo-700'
    };
  }
  if (c.includes('biotech') || c.includes('farmác') || c.includes('health')) {
    return {
      key: 'biotech',
      nameSk: 'Biotech & Zdravotníctvo',
      nameEn: 'Biotech & Healthcare',
      icon: '🧬',
      color: '#059669', // emerald-600
      badgeBg: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      borderClass: 'border-emerald-200 hover:border-emerald-500',
      selectedBgClass: 'bg-emerald-950 text-white border-emerald-700 shadow-md ring-2 ring-emerald-500',
      glowClass: 'bg-emerald-500/10 text-emerald-700'
    };
  }
  if (c.includes('automobil') || c.includes('spotreb') || c.includes('consumer')) {
    return {
      key: 'automotive',
      nameSk: 'Automobilový & Spotrebný',
      nameEn: 'Automotive & Consumer',
      icon: '🚗',
      color: '#ea580c', // orange-600
      badgeBg: 'bg-orange-50 text-orange-700 border-orange-200',
      borderClass: 'border-orange-200 hover:border-orange-500',
      selectedBgClass: 'bg-orange-950 text-white border-orange-700 shadow-md ring-2 ring-orange-500',
      glowClass: 'bg-orange-500/10 text-orange-700'
    };
  }
  if (c.includes('základné') || c.includes('basic') || c.includes('mining') || c.includes('materiál')) {
    return {
      key: 'materials',
      nameSk: 'Základné materiály & Ťažba',
      nameEn: 'Basic Materials & Mining',
      icon: '⛏️',
      color: '#d97706', // amber-600
      badgeBg: 'bg-amber-50 text-amber-800 border-amber-200',
      borderClass: 'border-amber-200 hover:border-amber-500',
      selectedBgClass: 'bg-amber-950 text-white border-amber-700 shadow-md ring-2 ring-amber-500',
      glowClass: 'bg-amber-500/10 text-amber-700'
    };
  }
  if (c.includes('financ') || c.includes('bank')) {
    return {
      key: 'finance',
      nameSk: 'Financie & Bankovníctvo',
      nameEn: 'Banking & Finance',
      icon: '🏦',
      color: '#0284c7', // sky-600
      badgeBg: 'bg-sky-50 text-sky-700 border-sky-200',
      borderClass: 'border-sky-200 hover:border-sky-500',
      selectedBgClass: 'bg-sky-950 text-white border-sky-700 shadow-md ring-2 ring-sky-500',
      glowClass: 'bg-sky-500/10 text-sky-700'
    };
  }
  if (c.includes('krypt') || c.includes('crypto')) {
    return {
      key: 'crypto',
      nameSk: 'Kryptomeny',
      nameEn: 'Cryptocurrencies',
      icon: '🪙',
      color: '#9333ea', // purple-600
      badgeBg: 'bg-purple-50 text-purple-700 border-purple-200',
      borderClass: 'border-purple-200 hover:border-purple-500',
      selectedBgClass: 'bg-purple-950 text-white border-purple-700 shadow-md ring-2 ring-purple-500',
      glowClass: 'bg-purple-500/10 text-purple-700'
    };
  }
  return {
    key: 'other',
    nameSk: 'Ostatné',
    nameEn: 'Other Assets',
    icon: '📊',
    color: '#64748b',
    badgeBg: 'bg-slate-100 text-slate-700 border-slate-200',
    borderClass: 'border-slate-200 hover:border-slate-400',
    selectedBgClass: 'bg-slate-900 text-white border-slate-900 shadow-md ring-2 ring-slate-500',
    glowClass: 'bg-slate-500/10 text-slate-700'
  };
};
